(ns measure.queries
  (:require [camel-snake-kebab.core :as csk]
            [camel-snake-kebab.extras :as cske]
            [cljs-bean.core :refer [->clj]]
            [clojure.string :as str]
            [common.db.effects.binary :as binary]
            [common.queries :refer [invalidate-queries]]
            [common.queries.http :as qhttp]
            [common.util :as util]
            [measure.messages :refer [ui-messages]]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as rfe]))

(defn enrich-reports
  "Enriches reports with adset name and user name"
  [reports adsets users]
  (let [adsets (map (fn [adset]
                      {:adset-id (:id adset)
                       :adset-name (:name adset)})
                    adsets)
        users (map (fn [user]
                     {:user-id (:id user)
                      :user-name (:name user)})
                   users)]
    (-> reports
        (->> (map (fn [{:keys [odc-user] :as report}]
                    (let [adset-ids (cond
                                      (seq (:adset-ids report)) (:adset-ids report)
                                      (:adset-id report) [(:adset-id report)]
                                      :else [])]
                      (-> (dissoc report :odc-user :adset-id)
                          (assoc :adset-ids adset-ids)
                          (assoc :adsets (filterv (comp (set adset-ids) :adset-id) adsets))
                          (assoc :user-id (:user-account-id odc-user)))))))
        (cond-> (not-empty users) (util/left-join users :user-id)))))

(defn transform-reports-response [response]
  (->> response
       ->clj
       :items
       (map #(cske/transform-keys csk/->kebab-case %))))

(defn reports-query
  [{:keys [advertiser-id]}]
  #js {:queryKey #js ["reports" advertiser-id]
       :queryFn (fn [^js _ctx]
                  (qhttp/get (qhttp/render :measure-api/reports {}
                                           {:advertiser_id advertiser-id
                                            :disable_pagination true})))
       :select transform-reports-response
       :staleTime (* 60 1000)
       :refetchInterval (fn [^js query]
                          (let [reports (some-> query .-state .-data .-items ->clj)]
                            (when-not (every? (comp #{"finished" "errored"} :state) reports)
                              10000)))})

(defn delete-report-mutation
  [{:keys [advertiser-id]}]
  #js {:mutationKey #js ["delete-report" advertiser-id]
       :mutationFn (fn [{:keys [id]}]
                     (rf/dispatch [:loader/open])
                     (qhttp/delete (qhttp/render :measure-api/report {:report-id id})))
       :onSuccess (fn [_]
                    (invalidate-queries {:queryKey ["reports" advertiser-id]})
                    (rf/dispatch [:loader/close])
                    (rf/dispatch [:response-handler/handle-response
                                  {:entity-name "report"
                                   :type :success
                                   :message (get-in ui-messages [:report :delete :success])}]))
       :onError (fn [response]
                  (rf/dispatch [:loader/close])
                  (rf/dispatch [:response-handler/handle-response
                                {:entity-name "report"
                                 :type :error
                                 :message (get-in ui-messages [:report :delete :failure])
                                 :response response}]))})

(defn download-report-mutation []
  #js {:mutationKey #js ["download-report"]
       :mutationFn (fn [{:keys [id]}]
                     (rf/dispatch [:loader/open])
                     (qhttp/get (qhttp/render :measure-api/report-download {:report-id id})
                                {:response-format :binary+filename}))
       :onSuccess (fn [{:keys [blob filename]}]
                    (binary/download blob filename)
                    (rf/dispatch [:loader/close])
                    (rf/dispatch [:response-handler/handle-response
                                  {:entity-name "report"
                                   :type :success
                                   :message (get-in ui-messages [:report :download :success])}]))
       :onError (fn [response]
                  (rf/dispatch [:loader/close])
                  (rf/dispatch [:response-handler/handle-response
                                {:entity-name "report"
                                 :type :error
                                 :message (get-in ui-messages [:report :download :failure])
                                 :response response}]))})

(defn report-schedules-query
  [{:keys [advertiser-id]}]
  #js {:queryKey #js ["report-schedules" advertiser-id]
       :queryFn (fn [^js _ctx]
                  (qhttp/get (qhttp/render :measure-api/report-schedules {}
                                           {:advertiser_id advertiser-id
                                            :disable_pagination true})))
       :select transform-reports-response
       :staleTime (* 60 1000)})

(defn create-report-schedule-mutation
  [{:keys [advertiser-id]}]
  #js {:mutationKey #js ["create-report-schedule" advertiser-id]
       :mutationFn (fn [payload]
                     (rf/dispatch [:loader/open])
                     (qhttp/post (qhttp/render :measure-api/report-schedule-create {})
                                 {:body (->> (merge payload {:advertiser-id advertiser-id})
                                             (cske/transform-keys csk/->snake_case))}))
       :onSuccess (fn [_]
                    (invalidate-queries {:queryKey ["report-schedules" advertiser-id]})
                    (rf/dispatch [:loader/close])
                    (rf/dispatch [:response-handler/handle-response
                                  {:entity-name "report schedule"
                                   :type :success
                                   :message (get-in ui-messages [:report-schedule :create :success])}]))
       :onError (fn [response]
                  (rf/dispatch [:loader/close])
                  (rf/dispatch [:response-handler/handle-response
                                {:entity-name "report schedule"
                                 :type :error
                                 :message (get-in ui-messages [:report-schedule :create :failure])
                                 :response response}]))})

(defn update-report-schedule-mutation
  [{:keys [advertiser-id]}]
  #js {:mutationKey #js ["update-report-schedule" advertiser-id]
       :mutationFn (fn [payload]
                     (rf/dispatch [:loader/open])
                     (let [id (:id payload)]
                       (qhttp/patch (qhttp/render :measure-api/report-schedule {:report-config-id id})
                                    {:body (->> (merge payload {:advertiser-id advertiser-id})
                                                (cske/transform-keys csk/->snake_case))})))
       :onSuccess (fn [_]
                    (invalidate-queries {:queryKey ["report-schedules" advertiser-id]})
                    (rf/dispatch [:loader/close])
                    (rf/dispatch [:response-handler/handle-response
                                  {:entity-name "report schedule"
                                   :type :success
                                   :message (get-in ui-messages [:report-schedule :edit :success])}]))
       :onError (fn [response]
                  (rf/dispatch [:loader/close])
                  (rf/dispatch [:response-handler/handle-response
                                {:entity-name "report schedule"
                                 :type :error
                                 :message (get-in ui-messages [:report-schedule :edit :failure])
                                 :response response}]))})

(defn delete-report-schedule-mutation
  [{:keys [advertiser-id]}]
  #js {:mutationKey #js ["delete-report-schedule" advertiser-id]
       :mutationFn (fn [{:keys [id]}]
                     (rf/dispatch [:loader/open])
                     (qhttp/delete (qhttp/render :measure-api/report-schedule {:report-config-id id})))
       :onSuccess (fn [_]
                    (invalidate-queries {:queryKey ["report-schedules" advertiser-id]})
                    (rf/dispatch [:loader/close])
                    (rf/dispatch [:response-handler/handle-response
                                  {:entity-name "report schedule"
                                   :type :success
                                   :message (get-in ui-messages [:report-schedule :delete :success])}]))
       :onError (fn [response]
                  (rf/dispatch [:loader/close])
                  (rf/dispatch [:response-handler/handle-response
                                {:entity-name "report schedule"
                                 :type :error
                                 :message (get-in ui-messages [:report-schedule :delete :failure])
                                 :response response}]))})

(defn report-from-schedule-mutation
  [{:keys [agency-id advertiser-id] :as url-context}]
  #js {:mutationKey #js ["report-from-schedule" agency-id advertiser-id]
       :mutationFn (fn [{:keys [id _type]}]
                     (rf/dispatch [:loader/open])
                     (qhttp/get (qhttp/render :measure-api/report-from-config {:report-config-id id})))
       :onSuccess (fn [_resp {:keys [type]}]
                    (invalidate-queries {:queryKey ["reports" advertiser-id]})
                    (rf/dispatch [:loader/close])
                    (rfe/navigate :advertiser/reports {:path-params url-context})
                    (rf/dispatch [:response-handler/handle-response
                                  {:entity-name "report schedule"
                                   :type :success
                                   :message (get-in ui-messages [:report :create (keyword type) :success])}]))
       :onError (fn [response {:keys [type]}]
                  (rf/dispatch [:loader/close])
                  (rf/dispatch [:response-handler/handle-response
                                {:entity-name "report schedule"
                                 :type :error
                                 :message (get-in ui-messages [:report :create (keyword type) :failure])
                                 :response response}]))})

(defn report-fields-query
  [{:keys [adset-ids]}]
  #js {:queryKey #js ["report-fields" adset-ids]
       :queryFn (fn [_]
                  (qhttp/get (qhttp/render :measure-api/report-fields {}
                                           {:adset_id (str/join "," adset-ids)})))
       :staleTime (* 60 1000)
       :enabled (boolean (seq adset-ids))})

(defn report-product-fields-query
  [{:keys [advertiser-id adset-ids]}]
  #js {:queryKey #js ["report-product-fields" advertiser-id adset-ids]
       :queryFn (fn [_]
                  (qhttp/get (qhttp/render :measure-api/product-store-fields {}
                                           {:advertiser_ids advertiser-id
                                            :adset_ids (str/join "," adset-ids)})))
       :staleTime (* 60 1000)
       :enabled (and (some? advertiser-id)
                     (boolean (seq adset-ids)))})

(defn create-unified-report-mutation
  [{:keys [advertiser-id]}]
  #js {:mutationKey #js ["create-unified-report" advertiser-id]
       :mutationFn (fn [payload]
                     (rf/dispatch [:loader/open])
                     (qhttp/post (qhttp/render :measure-api/unified-report {})
                                 {:body (->> (merge payload {:advertiser-id advertiser-id})
                                             (cske/transform-keys csk/->snake_case))}))
       :onSuccess (fn [_]
                    (invalidate-queries {:queryKey ["reports" advertiser-id]})
                    (rf/dispatch [:loader/close])
                    (rf/dispatch [:response-handler/handle-response
                                  {:entity-name "report"
                                   :type :success
                                   :message (get-in ui-messages [:report :create :custom :success])}]))
       :onError (fn [response]
                  (rf/dispatch [:loader/close])
                  (rf/dispatch [:response-handler/handle-response
                                {:entity-name "report"
                                 :type :error
                                 :message (get-in ui-messages [:report :create :custom :failure])
                                 :response response}]))})
